import { defaultShowtimeCutoffMinutes, oneHourInMs } from '@/constants/time';
import { BasicShowtime, MovieShowtimeItem, ShowtimeStatusType, TheaterShowtimeGroup } from '@/types/showtimes';

// TODO: make `offsetMinutes` configurable via CMS/Content API (ShowtimeCutoffMinutes / VRShowtimeCutoffMinutes) (PBI-138010).
type ShowtimeCutoffOptions = {
  now?: number;
  offsetMinutes?: number;
};

type ShowtimeDaySplit = {
  lateNightShowtimes: BasicShowtime[];
  sameDayShowtimes: BasicShowtime[];
};

const getLocalDateKey = (date: Date, timeZone?: string): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'numeric',
    timeZone,
    year: 'numeric',
  }).formatToParts(date).reduce<Record<string, string>>((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const resolveOffsetMs = (offsetMinutes: number | undefined): number =>
  (offsetMinutes ?? defaultShowtimeCutoffMinutes) * 60 * 1000;

export const isFutureShowtime = (
  showtime: Pick<BasicShowtime, 'startTime'>,
  options: ShowtimeCutoffOptions = {}
): boolean => {
  const nowMs = options.now ?? Date.now();
  const offsetMs = resolveOffsetMs(options.offsetMinutes);
  return showtime.startTime.getTime() + offsetMs > nowMs;
};

export const isActiveStatus = (status: ShowtimeStatusType): boolean =>
  status === ShowtimeStatusType.Saleable ||
  status === ShowtimeStatusType.PreSaleOnly ||
  status === ShowtimeStatusType.SoldOut;

const startingSoonWindowMs = 3 * oneHourInMs;

export const selectStartingSoonItems = <T>(
  items: T[],
  getShowtime: (item: T) => Pick<BasicShowtime, 'startTime' | 'status'>,
  options: { now?: number; windowMs?: number; } = {}
): T[] => {
  const now = options.now ?? Date.now();
  const cutoff = now + (options.windowMs ?? startingSoonWindowMs);

  return items
    .filter((item) => {
      const { startTime, status } = getShowtime(item);
      return isActiveStatus(status) && startTime.getTime() >= now && startTime.getTime() <= cutoff;
    })
    .sort((a, b) => getShowtime(a).startTime.getTime() - getShowtime(b).startTime.getTime());
};

export const filterFutureShowtimes = (
  showtimes: BasicShowtime[],
  options: ShowtimeCutoffOptions = {}
): BasicShowtime[] => {
  const now = options.now ?? Date.now();
  const offsetMinutes = options.offsetMinutes ?? defaultShowtimeCutoffMinutes;
  return showtimes.filter(showtime =>
    isFutureShowtime(showtime, { now, offsetMinutes })
  );
};

export const filterDisplayableShowtimes = (
  showtimes: BasicShowtime[],
  options: ShowtimeCutoffOptions = {}
): BasicShowtime[] => {
  const now = options.now ?? Date.now();
  const offsetMinutes = options.offsetMinutes ?? defaultShowtimeCutoffMinutes;

  return showtimes.filter(showtime =>
    isActiveStatus(showtime.status) && isFutureShowtime(showtime, { now, offsetMinutes })
  );
};

export const filterActiveTheaterShowtimeGroups = (
  groups: TheaterShowtimeGroup[],
  options: ShowtimeCutoffOptions = {}
): TheaterShowtimeGroup[] => {
  return groups
    .map(group => ({
      ...group,
      showtimes: filterDisplayableShowtimes(group.showtimes, options),
    }))
    .filter(group => group.showtimes.length > 0);
};

export const hasAnyActiveTheaterShowtime = (groups: TheaterShowtimeGroup[]): boolean =>
  groups.some(group =>
    group.showtimes.some(showtime => isActiveStatus(showtime.status))
  );

export const selectEligibleMovieShowtimeTheater = (
  theaters: MovieShowtimeItem[]
): MovieShowtimeItem | undefined => {
  const now = Date.now();
  const isEligible = (theater: MovieShowtimeItem): boolean =>
    hasAnyActiveTheaterShowtime(filterActiveTheaterShowtimeGroups(theater.showtimesByPrintType, { now, offsetMinutes: 0 }));

  return theaters.find(isEligible) ?? theaters[0];
};

export const selectTrailerShowtimeTheater = (
  theaters: MovieShowtimeItem[],
  preferredTheaterId?: number
): MovieShowtimeItem | undefined => {
  if (!theaters.length) return undefined;

  const preferred = preferredTheaterId
    ? theaters.find(theater => theater.theaterId === preferredTheaterId)
    : undefined;

  if (preferred) {
    const preferredActiveGroups = filterActiveTheaterShowtimeGroups(
      preferred.showtimesByPrintType,
      { offsetMinutes: 0 }
    );

    if (hasAnyActiveTheaterShowtime(preferredActiveGroups)) return preferred;
  }

  return selectEligibleMovieShowtimeTheater(theaters);
};

export const splitShowtimesByDayBoundary = (
  showtimes: BasicShowtime[],
  timeZone?: string
): ShowtimeDaySplit => {
  if (showtimes.length === 0) return { lateNightShowtimes: [], sameDayShowtimes: [] };

  let earliest = showtimes[0].startTime;
  for (let i = 1; i < showtimes.length; i++) {
    if (showtimes[i].startTime.getTime() < earliest.getTime()) {
      earliest = showtimes[i].startTime;
    }
  }
  const referenceDateKey = getLocalDateKey(earliest, timeZone);

  return showtimes.reduce<ShowtimeDaySplit>(
    (acc, showtime) => {
      if (!showtime.midnightShowtime && getLocalDateKey(showtime.startTime, timeZone) === referenceDateKey) {
        acc.sameDayShowtimes.push(showtime);
      } else {
        acc.lateNightShowtimes.push(showtime);
      }
      return acc;
    },
    { lateNightShowtimes: [], sameDayShowtimes: [] }
  );
};
